/**
 * L4: item detail view-model (01-references §6 S6), following the Mac DetailFields rules
 * (01 §4 rule 7): a one-time-code seed is never a row (a live code stands in), sensitive values
 * are masked until revealed, and the mask is a fixed length so it never leaks the value's length.
 */
import { CATEGORY_LABEL, fieldRef, type FieldRef, type FieldKind, type ItemField, type VaultItem, type Wallet } from "../contract.js";
import { otpNow, type OtpView } from "../detail/otp.js";
import { isRevealed, type Revealed } from "../detail/reveal.js";
import { isFavorite } from "./items.js";

export const MASK = "••••••••";

export interface DetailRow {
  ref: FieldRef;
  field: ItemField;
  kind: FieldKind;
  label: string;
  /** What the screen shows: the value, the mask, or the live code. Never an OTP seed. */
  display: string;
  masked: boolean;
  revealed: boolean;
  canReveal: boolean;
  pinned: boolean;
  otp?: OtpView;
}

export interface DetailModel {
  title: string;
  walletName: string;
  categoryLabel: string;
  favorite: boolean;
  rows: DetailRow[];
  notes: DetailRow | null;
  tags: [string, string][];
  modifiedLabel: string;
}

export interface DetailContext {
  revealed: Revealed;
  pinned: FieldRef[];
  favoriteOverrides: Record<string, boolean>;
  now: number;
}

function row(item: VaultItem, field: ItemField, ctx: DetailContext): DetailRow {
  const ref = fieldRef(item.id, field.id);
  const pinned = ctx.pinned.includes(ref);
  if (field.kind === "otp") {
    const otp = otpNow(field, ctx.now);
    return { ref, field, kind: field.kind, label: field.label, display: otp.grouped, masked: false, revealed: false, canReveal: false, pinned, otp };
  }
  const revealed = field.sensitive && isRevealed(ctx.revealed, ref, ctx.now);
  const masked = field.sensitive && !revealed;
  return { ref, field, kind: field.kind, label: field.label, display: masked ? MASK : field.value, masked, revealed, canReveal: field.sensitive, pinned };
}

export function detailModel(item: VaultItem, ctx: DetailContext, wallets: Wallet[]): DetailModel {
  const main = item.fields.filter((f) => f.kind !== "note").map((f) => row(item, f, ctx));
  const note = item.fields.find((f) => f.kind === "note");
  return {
    title: item.title,
    walletName: wallets.find((w) => w.id === item.wallet)?.name ?? item.wallet,
    categoryLabel: CATEGORY_LABEL[item.category],
    favorite: isFavorite(item, ctx.favoriteOverrides),
    rows: main,
    notes: note ? row(item, note, ctx) : null,
    tags: Object.entries(item.tags).filter(([k]) => k !== "type" && k !== "domain"),
    modifiedLabel: `Last edited ${new Date(item.modifiedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}`,
  };
}

/** What a copy puts on the clipboard: the value, or the current code for a one-time code. */
export function copyValue(field: ItemField, now: number): string {
  return field.kind === "otp" ? otpNow(field, now).code : field.value;
}
